import BlogArticleCard from '@/components/blog/BlogArticleCard';
import FilterBlock, { FilterBlock_Category, FilterBlock_Filter } from '@/components/FilterBlock';
import Image from '@/components/Image';
import Pagination from '@/components/Pagination';
import { BlogCategoryArticleFieldsFragment, BlogSearchArticleFieldsFragment, Maybe } from '@/graphql';
import gtm from '@/lib/gtm';
import useBreakpoint from '@/lib/hooks/useBreakpoint';
import { mediaBreakpointUp } from '@/styles/layout/utils';
import { remCalc } from '@/styles/typography/utils';
import { FC } from 'react';
import styled from 'styled-components';

const StickyFiltersThreshold = 84;

const CardImage = styled.div<{ objectPosition: string }>`
  img {
    object-fit: cover;
    object-position: ${(props) => props.objectPosition};
  }
`;

interface ArticleGridProps {
  entriesPerPage: number;
}

const ArticleGrid = styled.div<ArticleGridProps>`
  display: grid;
  grid-template-columns: 100%;
  gap: ${remCalc(20)};

  ${mediaBreakpointUp('tablet')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: repeat(${(props) => Math.ceil(props.entriesPerPage / 2) + 1}, auto);
  }

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: repeat(2, minmax(0, 1fr)) ${remCalc(270)};
    grid-template-rows: repeat(${(props) => Math.ceil(props.entriesPerPage / 2)}, auto);
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-template-columns: repeat(3, minmax(0, 1fr)) ${remCalc(270)};
    grid-template-rows: repeat(${(props) => Math.ceil(props.entriesPerPage / 3)}, auto);
    gap: ${remCalc(32)};
  }
`;

const FilterColumn = styled.div`
  margin-bottom: ${remCalc(20)};

  ${mediaBreakpointUp('tablet')} {
    grid-column: 1 / -1;
  }

  ${mediaBreakpointUp('desktop')} {
    grid-column: 3;
    grid-row: 1 / -1;
    margin-bottom: 0;

    & > div {
      position: sticky;
      top: ${remCalc(StickyFiltersThreshold)};
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-column: 4;
  }
`;

const StyledFilterBlock = styled(FilterBlock)`
  ${mediaBreakpointUp('desktop')} {
    padding-top: ${remCalc(30)};
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: ${remCalc(20)};

  ${mediaBreakpointUp('tablet')} {
    grid-column: 1 / span 2;
  }

  ${mediaBreakpointUp('desktop')} {
    grid-column: 1 / span 3;
    padding-top: ${remCalc(40)};
  }
`;

interface FilterableArticleGridProps {
  entries: Maybe<Maybe<BlogCategoryArticleFieldsFragment | BlogSearchArticleFieldsFragment>[]>;
  filtersCollapsible: boolean;
  filterCategories: FilterBlock_Category[];
  filterSelections: string[];
  totalEntries: number;
  entriesPerPage: number;
  currentPage: number;
  articleCategoryOverride?: string;
  hrefBuilder: (page: number) => string;
  onFilterToggle: (category: FilterBlock_Category, filter: FilterBlock_Filter, selected: boolean) => void;
  onPageChange: (page: number) => void;
}

const FilterableArticleGrid: FC<FilterableArticleGridProps> = (props) => {
  const [currentBreakpoint] = useBreakpoint();

  return (
    <ArticleGrid entriesPerPage={Math.min(props.entriesPerPage, props.entries?.length ?? 0)}>
      <FilterColumn>
        <div>
          <StyledFilterBlock
            collapsible={props.filtersCollapsible}
            categories={props.filterCategories}
            selections={props.filterSelections}
            onFilterToggle={props.onFilterToggle}
          />
        </div>
      </FilterColumn>

      {props.entries?.map((article, index) => {
        const image = article?.coverImage?.[0];

        const onClick = () => {
          gtm.track('click', {
            category: 'Article Cards',
            element_label: `Card | Position ${index + 1} | ${article?.title}`,
          });
        };

        return (
          <BlogArticleCard
            key={article?.id}
            title={article?.title!}
            href={article?.url!}
            category={
              props.articleCategoryOverride || (article as BlogSearchArticleFieldsFragment).blogCategories?.[0]?.title!
            }
            image={
              image && (
                <CardImage
                  objectPosition={
                    image?.focalPoint ? image.focalPoint.map((pos) => `${pos! * 100}%`).join(' ') : 'center'
                  }
                >
                  <Image src={image.url!} alt={image.altText!} fill />
                </CardImage>
              )
            }
            onClick={onClick}
          />
        );
      })}

      {(props.entries ?? []).length > 0 && (
        <PaginationWrapper>
          <Pagination
            pages={Math.ceil(props.totalEntries / props.entriesPerPage)}
            currentPage={props.currentPage}
            padding={1}
            margin={2}
            onPageChange={props.onPageChange}
            hrefBuilder={props.hrefBuilder}
          />
        </PaginationWrapper>
      )}
    </ArticleGrid>
  );
};

export default FilterableArticleGrid;
